import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLazyGetPrivateFileQuery } from "../../../store/api/fileApiSlice";
import { appColors } from "../../../constants/theme";
import { resolveStoredFileId } from "../../../utils/storedFile";

const educationStatusConfig = {
  Pending: { chipBg: appColors.warningBg, chipColor: appColors.warning, labelKey: "pendingreview" },
  Approved: { chipBg: appColors.successBg, chipColor: appColors.successDark, labelKey: "approved" },
  Rejected: { chipBg: appColors.errorBg, chipColor: appColors.errorDark, labelKey: "rejected" },
};

const normalizeEducationStatus = (rawStatus) => {
  if (rawStatus === "Pending" || rawStatus === "Approved" || rawStatus === "Rejected") return rawStatus;

  if (typeof rawStatus === "string") {
    const trimmed = rawStatus.trim();
    if (trimmed === "0") return "Pending";
    if (trimmed === "1") return "Approved";
    if (trimmed === "2") return "Rejected";

    const lower = trimmed.toLowerCase();
    if (lower === "pending") return "Pending";
    if (lower === "approved") return "Approved";
    if (lower === "rejected") return "Rejected";
  }

  if (rawStatus === 0) return "Pending";
  if (rawStatus === 1) return "Approved";
  if (rawStatus === 2) return "Rejected";
  return "Pending";
};

const guessExt = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const parts = fileName.split(".");
  return (parts[parts.length - 1] || "").toLowerCase();
};

const revokeBlobUrl = (maybeUrl) => {
  if (maybeUrl && typeof maybeUrl === "string" && maybeUrl.startsWith("blob:")) {
    window.URL.revokeObjectURL(maybeUrl);
  }
};

const EducationDetailDialog = ({
  open,
  onClose,
  education,
  personName = "",
  actions = null,
  title,
  maxWidth = "md",
  fullWidth = true,
}) => {
  const { t } = useTranslation();
  const [getPrivateFile] = useLazyGetPrivateFileQuery();

  const normalizedEducation = useMemo(() => {
    const e = education || {};
    return {
      doctorFirstName: e?.doctorFirstName ?? e?.DoctorFirstName ?? "",
      doctorLastName: e?.doctorLastName ?? e?.DoctorLastName ?? "",
      startYear: e?.startYear ?? e?.StartYear ?? "",
      endYear: e?.endYear ?? e?.EndYear ?? null,
      university: e?.university ?? e?.University ?? "",
      department: e?.department ?? e?.Department ?? "",
      status: normalizeEducationStatus(e?.status ?? e?.Status),
      certificateFileName: e?.certificateFileName ?? e?.CertificateFileName ?? null,
    };
  }, [education]);

  const displayName = useMemo(() => {
    if (personName) return personName;
    const combined = `${normalizedEducation.doctorFirstName} ${normalizedEducation.doctorLastName}`.trim();
    return combined || "";
  }, [personName, normalizedEducation.doctorFirstName, normalizedEducation.doctorLastName]);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreviewLoading(false);
      setPreviewError(null);
      setPreviewUrl((prev) => {
        revokeBlobUrl(prev);
        return null;
      });
      return;
    }

    let isActive = true;
    const fileName = normalizedEducation.certificateFileName;

    const run = async () => {
      if (!fileName) {
        setPreviewLoading(false);
        setPreviewError(null);
        setPreviewUrl((prev) => {
          revokeBlobUrl(prev);
          return null;
        });
        return;
      }

      setPreviewError(null);
      setPreviewLoading(true);
      setPreviewUrl((prev) => {
        revokeBlobUrl(prev);
        return null;
      });

      try {
        const fileId = resolveStoredFileId(fileName);
        if (!fileId) {
          setPreviewError(t("file_fetch_error") || "Dosya yüklenemedi.");
          setPreviewLoading(false);
          return;
        }
        const url = await getPrivateFile(fileId).unwrap();
        if (!isActive) return;

        if (url && typeof url === "string") {
          setPreviewUrl(url);
        } else {
          setPreviewError(t("file_fetch_error") || "Dosya yüklenemedi.");
        }
      } catch (e) {
        if (!isActive) return;
        setPreviewError(t("file_fetch_error") || "Dosya yüklenemedi.");
      } finally {
        if (!isActive) return;
        setPreviewLoading(false);
      }
    };

    run();
    return () => {
      isActive = false;
    };
  }, [open, normalizedEducation.certificateFileName, getPrivateFile, t]);

  const defaultActions = (
    <Button variant="outlined" onClick={onClose}>
      {t("cancel")}
    </Button>
  );

  const cfg = educationStatusConfig[normalizedEducation.status] ?? educationStatusConfig.Pending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth}>
      <DialogTitle sx={{ pr: 6 }}>{title || t("detail")}</DialogTitle>

      <DialogContent dividers>
        {!education ? null : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              {displayName ? (
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>
                  {displayName}
                </Typography>
              ) : null}
              <Typography sx={{ color: appColors.gray500, fontSize: 13 }}>
                {normalizedEducation.university} - {normalizedEducation.department} • {normalizedEducation.startYear} -{" "}
                {normalizedEducation.endYear ?? "-"}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={t(cfg.labelKey)}
                  sx={{
                    backgroundColor: cfg.chipBg,
                    color: cfg.chipColor,
                    border: `1px solid ${cfg.chipColor}`,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{t("learning_certificate")}</Typography>

              {normalizedEducation.certificateFileName ? (
                previewLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : previewError ? (
                  <Typography sx={{ color: appColors.errorDark, fontSize: 13 }}>{previewError}</Typography>
                ) : previewUrl ? (
                  <Box sx={{ width: "100%", border: `1px solid ${appColors.gray200}`, borderRadius: 2, p: 1 }}>
                    {(() => {
                      const ext = guessExt(normalizedEducation.certificateFileName);
                      if (ext === "pdf") {
                        return (
                          <iframe
                            title="certificate-preview"
                            src={previewUrl}
                            style={{ width: "100%", height: 520, border: 0 }}
                          />
                        );
                      }

                      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
                        return (
                          <img
                            src={previewUrl}
                            alt="certificate"
                            style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
                          />
                        );
                      }

                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography sx={{ color: appColors.gray500, fontSize: 13 }}>
                            {t("file_preview_not_supported") || "Bu dosya türü önizleme için desteklenmiyor."}
                          </Typography>
                          <Button variant="outlined" onClick={() => window.open(previewUrl, "_blank")}>
                            {t("view")}
                          </Button>
                        </Box>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography sx={{ color: appColors.gray500, fontSize: 13 }}>
                    {t("file_fetch_error") || "Dosya yüklenemedi."}
                  </Typography>
                )
              ) : (
                <Typography sx={{ color: appColors.gray500, fontSize: 13 }}>{t("no_data")}</Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>{actions ?? defaultActions}</DialogActions>
    </Dialog>
  );
};

export default EducationDetailDialog;
